import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect } from 'react';

// Single source of truth: AuthContext user (with normalized accountType). No duplicate Firestore read.
export default function AccountTypeRouter({ children }) {
  const { user, loading, authResolved } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const effectiveAccountType = user?.accountType ?? 'student';

  const getDashboardRoute = () => {
    switch (effectiveAccountType) {
      case 'student':
        return '/my-art-world';
      case 'mentor':
        if (user?.mentorType === 'organization' && user?.isPrimaryId) {
          return '/organization-dashboard';
        }
        return '/mentor-dashboard';
      default:
        return '/my-art-world';
    }
  };

  useEffect(() => {
    if (!authResolved || loading || !user) return;

    const currentPath = location.pathname;
    const expectedDashboard = getDashboardRoute();

    let grace = false;
    try {
      const ts = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
      grace = ts && (Date.now() - ts) < 120000;
    } catch (_) {}
    if (grace) return;
    if (currentPath === '/membership' || currentPath === '/mentor-dashboard') return;
    if (currentPath === '/onboarding/studio-access') return;
    if (currentPath === '/my-art-world' || currentPath === '/artist-wall' || currentPath === '/signup') return;
    if (currentPath === '/home') return;
    if (currentPath === '/my-art-history' || currentPath === '/my-art-stories' || currentPath === '/activity-dashboard') return;
    if (currentPath === '/learn-art-history' || currentPath.startsWith('/learn-art-history/')) return;

    // Do not auto-redirect from the public home page. Studio access validation should only
    // happen when the user explicitly attempts login/signup or navigates to protected areas.
    if (currentPath === '/') return;
    if (effectiveAccountType && currentPath.includes('/dashboard') && currentPath !== expectedDashboard) {
      navigate(expectedDashboard, { replace: true });
    }
  }, [authResolved, loading, user, location.pathname, effectiveAccountType, navigate]);

  if (!authResolved || loading) {
    return children;
  }

  if (!user) {
    return children;
  }

  const currentPath = location.pathname;
  const expectedDashboard = getDashboardRoute();

  let grace = false;
  try {
    const ts = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
    grace = ts && (Date.now() - ts) < 120000;
  } catch (_) {}

  if (grace) return children;
  if (currentPath === '/membership') return children;
  if (currentPath === '/mentor-dashboard') return children;
  if (currentPath === '/onboarding/studio-access') return children;
  if (currentPath === '/my-art-world') return children;
  if (currentPath === '/artist-wall') return children;
  if (currentPath === '/signup') return children;
  if (currentPath === '/home') return children;
  if (currentPath === '/my-art-history' || currentPath === '/my-art-stories' || currentPath === '/activity-dashboard') return children;
  if (currentPath === '/learn-art-history' || currentPath.startsWith('/learn-art-history/')) return children;

  if (currentPath === '/') return children;
  if (effectiveAccountType && currentPath.includes('/dashboard') && currentPath !== expectedDashboard) {
    return null;
  }

  return children;
}
